import React, { useEffect, useRef, useState } from "react";
import { Download, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { L } from "@/lib/i18n";
import {
  isSupported, precacheProject, getShowroomStatus, disableShowroom,
} from "@/lib/showroom";

/**
 * ShowroomButton — Phase C Feature 13.
 *
 * Top-bar entry-point for the offline showroom. Clicking "Showroom" opens
 * a small panel that:
 *  - Shows current status (ready / not enabled / not supported by browser)
 *  - Lets the user precache the whole project for offline use
 *  - Lets them clear the cache
 */
export default function ShowroomButton({ slug, lang }) {
  const [status, setStatus] = useState({ enabled: false, cached: 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const panelRef = useRef(null);
  const supported = isSupported();

  useEffect(() => {
    if (!supported) return;
    let alive = true;
    const refresh = () => getShowroomStatus(slug).then((next) => { if (alive) setStatus(next); }).catch(() => {});
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    return () => { alive = false; window.removeEventListener("focus", refresh); window.removeEventListener("storage", refresh); };
  }, [slug, supported, open]);

  useEffect(() => {
    if (!open) return undefined;
    const trigger = document.activeElement;
    panelRef.current?.focus();
    const escape = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("keydown", escape); trigger?.focus(); };
  }, [open]);

  const onPrecache = async () => {
    setBusy(true);
    setProgress({ done: 0, failed: 0, total: 0, complete: false });
    try {
      const result = await precacheProject(slug, (p) => setProgress(p));
      setProgress({ ...result, complete: result.ready === true && result.failed === 0 });
      const next = await getShowroomStatus(slug);
      setStatus(next);
      if (result.failed > 0) toast.warning(L(lang, "showroom_partial"));
      else toast.success(L(lang, "showroom_ready"));
    } catch (e) {
      setProgress((previous) => ({ ...previous, complete: false, error: true }));
      const next = await getShowroomStatus(slug).catch(() => ({ enabled: false, cached: 0, partial: true }));
      setStatus({ ...next, partial: !next.enabled });
      toast.error(L(lang, "showroom_failed"));
    } finally {
      setBusy(false);
    }
  };

  const onClear = async () => {
    setBusy(true);
    try {
      await disableShowroom(slug);
      setStatus({ enabled: false, cached: 0 });
      setProgress(null);
      toast.success(L(lang, "showroom_cleared"));
    } catch (_) {
      toast.error(L(lang, "showroom_failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;

  return (
    <div className="relative" data-testid="showroom-button-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="showroom-panel"
        data-testid="showroom-button"
        className={`pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 transition ${status.enabled ? "ring-1 ring-emerald-400/40" : ""}`}
        title={status.enabled ? L(lang, "showroom_ready") : L(lang, "showroom_mode")}
      >
        {status.enabled
          ? <Check className="h-4 w-4 text-emerald-300" />
          : <Download className="h-4 w-4" />}
        {L(lang, "showroom_mode")}
      </button>

      {open && (
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-labelledby="showroom-panel-title" id="showroom-panel" className="fixed inset-x-3 top-24 mx-auto w-auto max-w-sm max-h-[calc(100dvh-7rem)] overflow-y-auto bg-zinc-950 border border-white/10 rounded-xl shadow-2xl p-4 z-50 text-white" data-testid="showroom-panel">
          <div className="flex items-center justify-between">
            <div id="showroom-panel-title" className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, "showroom_mode")}</div>
            <button onClick={() => setOpen(false)} aria-label={L(lang, "close")} className="text-white/55 hover:text-white"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="mt-3 text-sm text-white/80 leading-relaxed">
            {L(lang, "showroom_description")}
          </div>
          {progress && (
            <div className="mt-3 text-xs text-white/60" data-testid="showroom-progress">
              {busy ? L(lang, "showroom_caching") : L(lang, progress.error ? "showroom_failed" : progress.complete ? "showroom_done" : "showroom_partial")}
              <div className="h-1 bg-white/10 mt-2 rounded overflow-hidden">
                <div className="h-full bg-[#C4A47C] transition-all" style={{ width: `${progress.total ? Math.round(((progress.done + progress.failed) / progress.total) * 100) : 0}%` }} />
              </div>
              <div className="mt-1"><bdi>{progress.done}/{progress.total}</bdi> {L(lang, "showroom_cached")}{progress.failed ? ` · ${progress.failed} ${L(lang, "showroom_failed_items")}` : ""}</div>
            </div>
          )}
          {status.enabled && !busy && (
            <div className="mt-3 text-xs text-emerald-300 inline-flex items-center gap-1.5" data-testid="showroom-status-enabled">
              <Check className="h-3.5 w-3.5" /> {L(lang, "showroom_ready")} — {status.cached} {L(lang, "showroom_items")}
            </div>
          )}
          {status.partial && !busy && (
            <div className="mt-3 text-xs text-amber-200" data-testid="showroom-status-partial">
              {L(lang, status.needsRefresh ? "showroom_needs_refresh" : "showroom_partial")}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <button
              onClick={onPrecache}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#C4A47C] text-zinc-900 text-xs uppercase tracking-wider hover:bg-[#d1b48d] disabled:opacity-50 transition"
              data-testid="showroom-precache"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {L(lang, status.enabled ? "showroom_refresh" : status.partial ? "showroom_retry" : "showroom_download")}
            </button>
            {(status.enabled || status.partial) && (
              <button
                onClick={onClear}
                disabled={busy}
                className="px-3 py-2 rounded-full border border-white/15 text-xs uppercase tracking-wider hover:bg-white/5 disabled:opacity-50"
                data-testid="showroom-clear"
              >
                {L(lang, "clear")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
